// Groups rollup rows by the drill level and produces one MetricTotals per entity
// — the drill-down table's equivalent of rollupMetrics.ts.
//
// This is deliberately thin: it partitions the rollup and edge rows by the drill
// key and hands each partition to buildTotalsFromRollup. The alternative was a
// third reduction with its own copy of the mask arithmetic, which is how two
// implementations of one formula drift apart.

import {
  buildTotalsFromRollup,
  matchesScope,
  type DvmWeekRow,
  type RollupSiteWeekRow,
} from "./rollupMetrics";
import type { MetricTotals } from "./metrics";
import type { SchedulingRow } from "../types/scheduling";
import type { DrillLevel, ScopeFilters } from "../types/filters";
import { peerScope } from "./drillScope";

export interface RollupDrillEntity {
  id: string;
  label: string;
  siteCount: number;
  totals: MetricTotals;
}

// Matches useDrillData's groupRows: the site label carries its id, the DVM label
// is the doctor's name, everything else is the dimension value itself.
function keyOf(
  row: {
    country_code?: string | null;
    division_name?: string | null;
    group_name?: string | null;
    location_id?: string | null;
    resource_id?: string | null;
  },
  level: DrillLevel
): string | null {
  switch (level) {
    case "country":
      return row.country_code ?? null;
    case "division":
      return row.division_name ?? null;
    case "group":
      return row.group_name ?? null;
    case "site":
      return row.location_id ?? null;
    case "dvm":
      return row.resource_id ?? null;
  }
}

function labelOf(
  row: {
    country_code?: string | null;
    division_name?: string | null;
    group_name?: string | null;
    location_id?: string | null;
    location_name?: string | null;
    resource_id?: string | null;
    resource_full_name?: string | null;
  },
  level: DrillLevel
): string {
  switch (level) {
    case "site":
      return `${row.location_id} - ${row.location_name ?? ""}`;
    case "dvm":
      return String(row.resource_full_name ?? row.resource_id ?? "");
    default:
      return keyOf(row, level) ?? "";
  }
}

export interface RollupDrillInput {
  siteWeeks: RollupSiteWeekRow[];
  dvmWeeks: DvmWeekRow[];
  edgeRows: SchedulingRow[];
  scope: ScopeFilters;
  drillLevel: DrillLevel;
  excludeZeroApptDays: boolean;
}

export function buildDrillFromRollup(input: RollupDrillInput): RollupDrillEntity[] {
  const { scope, drillLevel, excludeZeroApptDays } = input;

  // Scope has to be applied HERE, not assumed to have been applied at fetch.
  //
  // Only the WIDE doctor rows come back scoped. The site-week and edge rows are
  // fetched unscoped on purpose -- that is what makes the NVA benchmark
  // possible (see rollupFetch.ts) -- so a drill table built from them listed
  // every site in the company no matter what was selected. Picking one site
  // left the KPI cards, the charts and the pinned averages all correctly
  // narrowed while the table below them still showed 276 sites.
  //
  // The raw path never had this: useDrillData calls filterRowsByScope on its
  // rows before grouping them. This is the same filter, on the rollup row
  // shapes, and matchesScope is the predicate buildTotalsFromRollup already
  // uses -- so the rows a bucket is built from are exactly the rows its totals
  // are summed from.
  const peers = peerScope(scope, drillLevel);
  const siteWeeks = input.siteWeeks.filter((r) => matchesScope(r, peers));
  const dvmWeeks = input.dvmWeeks.filter((r) => matchesScope(r, peers));
  const edgeRows = input.edgeRows.filter((r) => matchesScope(r, peers));

  // Drilling to DVM needs per-doctor sums, which only the wide doctor rows carry.
  // Site-week rows have no resource_id, so they cannot be partitioned by doctor
  // at all -- grouping them by a null key would collapse every doctor into one.
  // The peer scope never carries a resourceId, so a doctor selection no longer
  // forces the measure source: at Site level the site-week rows can serve, and
  // they are the complete unscoped set.
  const measuresFromDvmWeeks = drillLevel === "dvm";

  const siteBuckets = new Map<string, RollupSiteWeekRow[]>();
  const dvmBuckets = new Map<string, DvmWeekRow[]>();
  const edgeBuckets = new Map<string, SchedulingRow[]>();
  const labels = new Map<string, string>();
  const sites = new Map<string, Set<string>>();

  // An entity only exists, and a site only counts, if something SURVIVES the
  // filter. Registering them before checking that produced two visible bugs:
  // a doctor whose only rows were zero-appointment days appeared as an extra
  // all-zero entity, and siteCount included sites that contributed nothing --
  // which changes the DVMs/day column. Both showed up as 12 of 100 cases
  // failing, every one of them excludeZeroApptDays.
  //
  // The doctor-week day mask is the authority for survival: day_mask_nonzero is
  // exactly which days had appointments. So entity membership and siteCount are
  // both derived from masks (and from surviving edge rows), never from the mere
  // presence of a rollup row.
  const label = (key: string, text: string) => {
    if (!labels.has(key)) labels.set(key, text);
  };
  const countSite = (key: string, locationId: string | null | undefined) => {
    if (!locationId) return;
    if (!sites.has(key)) sites.set(key, new Set());
    sites.get(key)!.add(locationId);
  };

  // Site-week rows cannot be keyed by doctor, so they are only partitioned when
  // they are the measure source. They never establish entity membership on their
  // own -- the masks do that.
  if (!measuresFromDvmWeeks) {
    for (const r of siteWeeks) {
      const key = keyOf(r, drillLevel);
      if (key === null) continue;
      if (!siteBuckets.has(key)) siteBuckets.set(key, []);
      siteBuckets.get(key)!.push(r);
      label(key, labelOf(r, drillLevel));
    }
  }

  // Doctor rows are always partitioned: they carry the day masks, and every
  // distinct count comes from those regardless of the drill level.
  const surviving = new Set<string>();
  for (const r of dvmWeeks) {
    const key = keyOf(r, drillLevel);
    if (key === null) continue;
    if (!dvmBuckets.has(key)) dvmBuckets.set(key, []);
    dvmBuckets.get(key)!.push(r);
    label(key, labelOf(r, drillLevel));
    const mask = excludeZeroApptDays ? r.day_mask_nonzero : r.day_mask;
    if (mask !== 0) {
      surviving.add(key);
      countSite(key, r.location_id);
    }
  }

  for (const r of edgeRows) {
    if (excludeZeroApptDays && Number(r.completed_appointment_minutes) <= 0) continue;
    const key = keyOf(r, drillLevel);
    if (key === null || key === "") continue;
    if (!edgeBuckets.has(key)) edgeBuckets.set(key, []);
    edgeBuckets.get(key)!.push(r);
    label(key, labelOf(r, drillLevel));
    surviving.add(key);
    countSite(key, r.location_id);
  }

  // Only entities with a surviving contribution. Under excludeZeroApptDays this
  // is what drops the all-zero rows the oracle never produces.
  return Array.from(surviving).map((key) => ({
    id: key,
    label: labels.get(key) ?? key,
    siteCount: sites.get(key)?.size ?? 0,
    totals: buildTotalsFromRollup({
      siteWeeks: siteBuckets.get(key) ?? [],
      dvmWeeks: dvmBuckets.get(key) ?? [],
      edgeRows: edgeBuckets.get(key) ?? [],
      // Already applied at the top of this function, so re-applying it per
      // partition would be a no-op that only risks disagreeing with itself.
      // (This comment used to say "when the rows were fetched", which was the
      // mistaken assumption behind the bug above.)
      scope: {
        countryCode: null,
        divisionName: null,
        groupName: null,
        locationId: null,
        resourceId: null,
      },
      excludeZeroApptDays,
      measuresFromDvmWeeks,
    }),
  }));
}
